import React, { useState } from 'react';
import { deleteSchedule } from '../../../api/adminApi';

/**
 * Deletes the classes behind one calendar row, behind a confirmation that names
 * what will be removed.
 *
 * **Deleting a Schedule is not a cheap operation.** Four children cascade —
 * `schedule_contents`, `schedule_submission_requirements`, `llm_match_logs` and
 * `schedule_submissions` (student work) — so the dialog is not a generic "are
 * you sure": it asks the server first with `dryRun`, then states the class and
 * the counts it would take with it. The server refuses outright when student
 * submissions or remarks exist, and that refusal is shown here rather than
 * being reported as a failure.
 *
 * A grouped calendar row is one class across every batch in the group, so this
 * deletes every schedule id behind it — the same set `RescheduleControl` moves.
 * **To move a class, use that instead:** delete-and-recreate is exactly what the
 * in-place date update exists to avoid.
 */
const DeleteScheduleControl = ({ scheduleIds = [], label, onDeleted }) => {
  const [stage, setStage] = useState('idle');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  if (scheduleIds.length === 0) return null;

  const openConfirm = async () => {
    setStage('checking');
    setError(null);
    try {
      // One dry run per id: a grouped row is the same class across batches, and
      // any one of them carrying student work blocks the whole row.
      const previews = await Promise.all(
        scheduleIds.map((scheduleId) => deleteSchedule(scheduleId, true))
      );
      const impact = previews.reduce((total, entry) => ({
        contents: total.contents + (entry.impact?.contents || 0),
        submissionRequirements:
          total.submissionRequirements + (entry.impact?.submissionRequirements || 0),
      }), { contents: 0, submissionRequirements: 0 });

      setPreview({ impact, schedule: previews[0]?.schedule || null });
      setStage('confirming');
    } catch (checkError) {
      setError(checkError?.response?.data?.message || 'Could not check what this would remove.');
      setStage('idle');
    }
  };

  const confirm = async () => {
    setStage('deleting');
    setError(null);
    try {
      for (const scheduleId of scheduleIds) {
        await deleteSchedule(scheduleId, false);
      }
      setStage('done');
      if (onDeleted) onDeleted({ scheduleIds });
    } catch (deleteError) {
      setError(deleteError?.response?.data?.message || 'Could not delete this class.');
      setStage('confirming');
    }
  };

  if (stage === 'done') {
    return <span className="cal-tl-deleted">Deleted</span>;
  }

  if (stage === 'confirming' && preview) {
    const { impact } = preview;
    return (
      <div className="cal-deleteconfirm" role="alertdialog" aria-label="Confirm delete">
        <div className="cal-deleteconfirm-body">
          <strong>Delete {label || 'this class'}?</strong>
          <div className="cal-deleteconfirm-detail">
            {scheduleIds.length > 1
              ? `${scheduleIds.length} schedule rows, one per batch in this group.`
              : 'One schedule row.'}
            {impact.contents > 0 && ` Unlinks ${impact.contents} content link(s).`}
            {impact.submissionRequirements > 0
              && ` Removes ${impact.submissionRequirements} submission requirement(s).`}
          </div>
          <div className="cal-deleteconfirm-warn">
            This cannot be undone. To move the class to another date, cancel and use
            Reschedule instead — that keeps its contents and history.
          </div>
          {error && <div className="cal-error">{error}</div>}
        </div>

        <div className="cal-deleteconfirm-actions">
          <button
            type="button"
            className="sb-btn"
            onClick={() => { setStage('idle'); setPreview(null); setError(null); }}
            disabled={stage === 'deleting'}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sb-btn sb-btn-danger"
            onClick={confirm}
            disabled={stage === 'deleting'}
          >
            {stage === 'deleting' ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="cal-tl-delete"
        onClick={openConfirm}
        disabled={stage === 'checking'}
      >
        <i className="bi bi-trash3" aria-hidden="true" />{' '}
        {stage === 'checking' ? 'Checking…' : 'Delete'}
      </button>
      {error && <span className="cal-error">{error}</span>}
    </>
  );
};

export default DeleteScheduleControl;
